import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getProgramClasses } from '../../services/programClassService';
import { ProgramClass } from '../../types';
import {
  MagnifyingGlassIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  AcademicCapIcon,
  ArrowsRightLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface StudentProfile {
  id: string;
  username: string;
  name: string;
  email: string;
  phone: string | null;
  user_type: 'adult' | 'minor' | 'institution' | 'admin';
  birthdate: string | null;
  guardian_name: string | null;
  guardian_phone: string | null;
}

interface EnrollmentRecord {
  id: string;
  application_number: string;
  student_name: string;
  status: 'registered' | 'waiting' | 'cancelled';
  payment_status: 'pending' | 'completed' | 'expired' | 'cancelled' | 'refunded' | null;
  payment_amount: number | null;
  created_at: string;
  program_name: string;
  class_name: string;
  class_id: string;
  program_id: string;
  course_start_date: string;
  course_end_date: string;
  instructor: string | null;
}

interface StudentWithHistory {
  profile: StudentProfile;
  enrollments: EnrollmentRecord[];
}

interface TransferModalState {
  isOpen: boolean;
  enrollment: EnrollmentRecord | null;
}

const StudentHistory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<StudentWithHistory[]>([]);

  // 반 변경 모달 상태
  const [transferModal, setTransferModal] = useState<TransferModalState>({
    isOpen: false,
    enrollment: null,
  });
  const [availableClasses, setAvailableClasses] = useState<ProgramClass[]>([]);
  const [selectedNewClassId, setSelectedNewClassId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      alert('검색어를 입력해주세요.');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      // 1. 회원 검색 (이름, 아이디, 연락처)
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, name, email, phone, user_type, birthdate, guardian_name, guardian_phone')
        .or(`name.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .neq('user_type', 'admin')
        .order('name')
        .limit(20);

      if (profileError) {
        console.error('Error searching profiles:', profileError);
        setResults([]);
        return;
      }

      if (!profiles || profiles.length === 0) {
        setResults([]);
        return;
      }

      // 2. 각 회원의 수강 이력 조회
      const studentIds = profiles.map(p => p.id);

      const { data: applications, error: appError } = await supabase
        .from('applications')
        .select(`
          id,
          application_number,
          student_name,
          status,
          payment_status,
          payment_amount,
          created_at,
          program_classes:class_id (
            class_name,
            course_start_date,
            course_end_date,
            instructor,
            programs:program_id (
              name
            )
          )
        `)
        .in('student_id', studentIds)
        .order('created_at', { ascending: false });

      if (appError) {
        console.error('Error loading applications:', appError);
      }

      // 3. 회원별로 수강 이력 그룹화
      const enrollmentMap: Record<string, EnrollmentRecord[]> = {};

      applications?.forEach((app: any) => {
        const studentId = app.student_id || profiles.find(p =>
          applications.some((a: any) => a.id === app.id)
        )?.id;

        // student_id를 다시 조회해야 함
      });

      // student_id 포함해서 다시 조회
      const { data: appsWithStudent } = await supabase
        .from('applications')
        .select(`
          id,
          application_number,
          student_id,
          student_name,
          class_id,
          program_id,
          status,
          payment_status,
          payment_amount,
          created_at,
          program_classes:class_id (
            class_name,
            course_start_date,
            course_end_date,
            instructor,
            programs:program_id (
              name
            )
          )
        `)
        .in('student_id', studentIds)
        .order('created_at', { ascending: false });

      appsWithStudent?.forEach((app: any) => {
        if (!enrollmentMap[app.student_id]) {
          enrollmentMap[app.student_id] = [];
        }

        enrollmentMap[app.student_id].push({
          id: app.id,
          application_number: app.application_number,
          student_name: app.student_name,
          status: app.status,
          payment_status: app.payment_status,
          payment_amount: app.payment_amount,
          created_at: app.created_at,
          class_id: app.class_id,
          program_id: app.program_id,
          program_name: app.program_classes?.programs?.name || '-',
          class_name: app.program_classes?.class_name || '-',
          course_start_date: app.program_classes?.course_start_date || '-',
          course_end_date: app.program_classes?.course_end_date || '-',
          instructor: app.program_classes?.instructor || null,
        });
      });

      // 4. 결과 조합
      const studentsWithHistory: StudentWithHistory[] = profiles.map(profile => ({
        profile: profile as StudentProfile,
        enrollments: enrollmentMap[profile.id] || [],
      }));

      setResults(studentsWithHistory);
    } catch (error) {
      console.error('Error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 반 변경 모달 열기
  const openTransferModal = async (enrollment: EnrollmentRecord) => {
    setTransferModal({ isOpen: true, enrollment });
    setSelectedNewClassId('');
    setClassesLoading(true);

    try {
      const classes = await getProgramClasses(enrollment.program_id);
      // 현재 반 제외
      const otherClasses = classes.filter(c => c.id !== enrollment.class_id);
      setAvailableClasses(otherClasses);
    } catch (error) {
      console.error('Error loading classes:', error);
      setAvailableClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  // 반 변경 모달 닫기
  const closeTransferModal = () => {
    setTransferModal({ isOpen: false, enrollment: null });
    setAvailableClasses([]);
    setSelectedNewClassId('');
  };

  // 반 변경 실행
  const handleTransfer = async () => {
    if (!transferModal.enrollment || !selectedNewClassId) return;

    const selectedClass = availableClasses.find(c => c.id === selectedNewClassId);
    if (!selectedClass) return;

    const confirmMessage = `${transferModal.enrollment.student_name}님의 반을 변경하시겠습니까?\n\n` +
      `강좌: ${transferModal.enrollment.program_name}\n` +
      `변경 전: ${transferModal.enrollment.class_name}\n` +
      `변경 후: ${selectedClass.class_name}`;

    if (!window.confirm(confirmMessage)) return;

    setTransferLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('admin-transfer-class', {
        body: {
          application_id: transferModal.enrollment.id,
          new_class_id: selectedNewClassId,
        },
      });

      if (error) {
        throw new Error(error.message || '반 변경 중 오류가 발생했습니다.');
      }

      if (!data?.success) {
        const errorMessage = data?.error?.message || '반 변경 중 오류가 발생했습니다.';
        alert(errorMessage);
        return;
      }

      let successMessage = `반 변경이 완료되었습니다.\n\n` +
        `${data.data.program_name}\n` +
        `${data.data.old_class_name} → ${data.data.new_class_name}`;

      if (data.data.promoted) {
        successMessage += `\n\n※ 기존 반 대기자 ${data.data.promoted.student_name}님이 자동 승격되었습니다.`;
      }

      alert(successMessage);
      closeTransferModal();

      // 데이터 새로고침
      await handleSearch();
    } catch (error: any) {
      console.error('Transfer error:', error);
      alert(error.message || '반 변경 중 오류가 발생했습니다.');
    } finally {
      setTransferLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'registered':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">접수완료</span>;
      case 'waiting':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">대기중</span>;
      case 'cancelled':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">취소</span>;
      default:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getPaymentBadge = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">결제완료</span>;
      case 'pending':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">입금대기</span>;
      case 'expired':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">기한만료</span>;
      case 'refunded':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">환불완료</span>;
      default:
        return null;
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case 'adult': return '14세 이상';
      case 'minor': return '14세 미만';
      case 'institution': return '기관';
      default: return userType;
    }
  };

  // 시간 포맷 (HH:MM)
  const formatTime = (time: string | null | undefined) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  return (
    <div>
      {/* 페이지 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">수강생 이력 조회</h1>
        <p className="text-sm text-gray-600 mt-1">수강생을 검색하여 전체 수강 이력을 확인할 수 있습니다.</p>
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="이름, 아이디, 연락처로 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? '검색중...' : '검색'}
          </button>
        </div>
      </div>

      {/* 검색 결과 */}
      {loading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-sm text-gray-500">검색 중...</p>
        </div>
      ) : searched && results.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-sm text-gray-500">검색 결과가 없습니다.</p>
        </div>
      ) : results.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            검색 결과: <span className="font-bold text-indigo-600">{results.length}</span>명
          </p>

          {results.map((student) => (
            <div key={student.profile.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* 학생 정보 헤더 */}
              <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-gray-900">{student.profile.name}</span>
                        <span className="text-sm text-gray-500">({student.profile.username})</span>
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {getUserTypeLabel(student.profile.user_type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        {student.profile.phone && (
                          <div className="flex items-center gap-1">
                            <PhoneIcon className="h-4 w-4" />
                            <span>{student.profile.phone}</span>
                          </div>
                        )}
                        {student.profile.email && (
                          <div className="flex items-center gap-1">
                            <EnvelopeIcon className="h-4 w-4" />
                            <span>{student.profile.email}</span>
                          </div>
                        )}
                      </div>
                      {student.profile.guardian_phone && (
                        <div className="text-xs text-blue-600 mt-1">
                          보호자: {student.profile.guardian_name} {student.profile.guardian_phone}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">{student.enrollments.length}</div>
                    <div className="text-xs text-gray-500">수강 이력</div>
                  </div>
                </div>
              </div>

              {/* 수강 이력 */}
              <div className="p-5">
                {student.enrollments.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    <AcademicCapIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                    수강 이력이 없습니다.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <AcademicCapIcon className="h-4 w-4" />
                      수강 이력
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">강좌</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">반</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">교육기간</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">강사</th>
                            <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">신청상태</th>
                            <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">결제상태</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600 whitespace-nowrap">결제금액</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">신청일</th>
                            <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">관리</th>
                          </tr>
                        </thead>
                        <tbody>
                          {student.enrollments.map((enrollment) => (
                            <tr key={enrollment.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2.5 px-3 font-medium text-gray-900 whitespace-nowrap">
                                {enrollment.program_name}
                              </td>
                              <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap">
                                {enrollment.class_name !== '기본반' ? enrollment.class_name : '-'}
                              </td>
                              <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap">
                                {enrollment.course_start_date} ~ {enrollment.course_end_date}
                              </td>
                              <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap">
                                {enrollment.instructor || '-'}
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {getStatusBadge(enrollment.status)}
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {getPaymentBadge(enrollment.payment_status)}
                              </td>
                              <td className="py-2.5 px-3 text-right text-gray-900 whitespace-nowrap">
                                {enrollment.payment_amount ? `${enrollment.payment_amount.toLocaleString()}원` : '-'}
                              </td>
                              <td className="py-2.5 px-3 text-gray-500 whitespace-nowrap">
                                {new Date(enrollment.created_at).toLocaleDateString('ko-KR')}
                              </td>
                              <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                {enrollment.status !== 'cancelled' && (
                                  <button
                                    onClick={() => openTransferModal(enrollment)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                                    title="반 변경"
                                  >
                                    <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
                                    반 변경
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-4 text-gray-500">수강생 이름, 아이디, 연락처를 검색해주세요.</p>
        </div>
      )}

      {/* 반 변경 모달 */}
      {transferModal.isOpen && transferModal.enrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeTransferModal}></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">반 변경</h3>
              <button
                onClick={closeTransferModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* 모달 내용 */}
            <div className="px-6 py-4 space-y-4">
              {/* 현재 정보 */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="text-sm">
                  <span className="text-gray-500">학생:</span>{' '}
                  <span className="font-medium text-gray-900">{transferModal.enrollment.student_name}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">강좌:</span>{' '}
                  <span className="font-medium text-gray-900">{transferModal.enrollment.program_name}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">현재 반:</span>{' '}
                  <span className="font-medium text-indigo-600">{transferModal.enrollment.class_name}</span>
                </div>
              </div>

              {/* 변경할 반 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">변경할 반 선택</label>
                {classesLoading ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    반 목록을 불러오는 중...
                  </div>
                ) : availableClasses.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500 bg-gray-50 rounded-lg">
                    변경 가능한 반이 없습니다.
                  </div>
                ) : (
                  <select
                    value={selectedNewClassId}
                    onChange={(e) => setSelectedNewClassId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">반을 선택해주세요</option>
                    {availableClasses.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.class_name}
                        {cls.instructor ? ` (${cls.instructor})` : ''}
                        {cls.course_start_time ? ` ${formatTime(cls.course_start_time)}~${formatTime(cls.course_end_time)}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeTransferModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleTransfer}
                disabled={!selectedNewClassId || transferLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {transferLoading ? '변경 중...' : '반 변경'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentHistory;
